import mixpanel from 'mixpanel-browser';
import { INTERNAL_USER_KEY } from '@/utils/analytics/storageKeys';
import {
  DataLoadCompletedProps,
  GetStartedCompletedProps,
  GraphCreatedProps,
  GraphResultsViewedProps,
  GroupAttributionProps,
  OnboardingPath,
  PageViewedProps,
  QueryInstalledProps,
  GraphDeletedProps,
  OrganizationInvitationCreatedProps,
  QueryRunCompletedProps,
  SchemaModifiedProps,
  SchemaSavedProps,
  SignInCompletedProps,
  TermsAcceptedProps,
} from '@/utils/analytics/types';
import { updateOnboardingPath, updateUserProfile, SyncUserProfileInput } from '@/utils/analytics/profiles';
import { resolveAnalyticsEnvironment } from '@/utils/analytics/taxonomy';
import { UserInfo } from '@/lib/models';

export enum AppToolType {
  GraphStudio = 'Graph Studio',
  Insights = 'Insights',
  AdminPortal = 'Admin Portal',
  GraphQL = 'GraphQL',
}

function buildGroupEventProps(attribution: GroupAttributionProps): Record<string, string[]> {
  const props: Record<string, string[]> = {};

  if (attribution.organization_id) {
    props.organization_id = [attribution.organization_id];
  }
  if (attribution.workgroup_id) {
    props.workgroup_id = [attribution.workgroup_id];
  }
  if (attribution.workspace_id) {
    props.workspace_id = [attribution.workspace_id];
  }

  return props;
}

function isInternalEmail(email?: string): boolean {
  return !!email?.toLowerCase().endsWith('tigergraph.com');
}

function readInternalUserFlag(): boolean {
  try {
    return window.localStorage.getItem(INTERNAL_USER_KEY) === 'true';
  } catch {
    return false;
  }
}

function writeInternalUserFlag(isInternal: boolean) {
  try {
    if (isInternal) {
      window.localStorage.setItem(INTERNAL_USER_KEY, 'true');
    } else {
      window.localStorage.removeItem(INTERNAL_USER_KEY);
    }
  } catch {
    // ignore
  }
}

function extractGroupAttribution(props: Record<string, unknown>): GroupAttributionProps {
  const organizationId = props.organization_id;
  const workgroupId = props.workgroup_id;
  const workspaceId = props.workspace_id;

  return {
    organization_id: typeof organizationId === 'string' ? organizationId : undefined,
    workgroup_id: typeof workgroupId === 'string' ? workgroupId : undefined,
    workspace_id: typeof workspaceId === 'string' ? workspaceId : undefined,
  };
}

class AnalyticsService {
  private userEmail = '';
  private knownInternalUser = readInternalUserFlag();
  private env = '';
  // False when mixpanel.init() failed (most often a missing token). Every method below
  // becomes a no-op rather than throwing from a module-level constructor and taking the
  // whole app down with it.
  private initialized = false;
  // Whether mixpanel.identify() has been called for the current session. Profile writes
  // must land on the identified distinct_id, not the pre-login anonymous one, otherwise
  // the same logical user ends up with profile data split across two distinct_ids.
  private hasIdentified = false;
  private pendingProfileWrites: Array<() => void> = [];

  constructor() {
    this.init();
  }

  init() {
    this.initialized = false;
    this.env = resolveAnalyticsEnvironment();

    const token = import.meta.env.VITE_MIX_Panel_Key;
    if (!token) {
      return;
    }

    try {
      mixpanel.init(token, {
        persistence: 'localStorage',
        // GeoIP enrichment is on so Mixpanel populates the reserved $country_code.
        // $city and $region come along with it and are not reliable enough to report
        // on — country is stable under the same conditions that made them drift.
        ip: true,
      });
      mixpanel.register({
        'Analytics Version': 1.1,
        Product: 'TigerGraph Savanna',
        environment: this.env,
        event_schema_version: 1,
      });
      this.initialized = true;
    } catch (error) {
      // Leave initialized false so the app runs without analytics.
    }
  }

  /**
   * Check if the current user should be excluded from tracking.
   * Users with emails ending in "tigergraph.com" are excluded in production only, so
   * dev and staging keep tracking internal users and the funnel stays testable.
   * cloud-universe applies the matching skip on the organization owner's email.
   */
  private shouldSkipTracking(): boolean {
    if (this.env !== 'production') {
      return false;
    }
    // Once an email is known — from login, or from a pre-auth page that carries one — the
    // actual account decides. Before that, pre-authentication events (signup,
    // email_verification, terms_accepted) have no identity at all, so fall back to whether
    // a TigerGraph account has ever signed in on this browser. It closes the common case,
    // staff retesting the funnel in their own profile, but not a first signup in a clean one.
    if (this.userEmail) {
      return isInternalEmail(this.userEmail);
    }
    return this.knownInternalUser;
  }

  private setUserEmail(email: string) {
    this.userEmail = email;
    this.knownInternalUser = isInternalEmail(email);
    // Cleared for a non-internal account so a shared browser stops suppressing their events.
    writeInternalUserFlag(this.knownInternalUser);
  }

  /**
   * Record the email a pre-authentication page already knows (the verification page carries
   * it in the URL) so the internal-user filter applies before login.
   */
  identifyPreAuthUser(email?: string) {
    if (email) {
      this.setUserEmail(email);
    }
  }

  private canTrack(): boolean {
    return this.initialized && !this.shouldSkipTracking();
  }

  /**
   * Send events to mixpanel.
   *
   * @param  {string} eventName event name
   * @param  {object} props? event properties
   */
  track(eventName: string, props?: object, options?: { flush?: boolean }) {
    if (!this.canTrack()) {
      return;
    }

    try {
      if (options?.flush) {
        // send_immediately + sendBeacon is the typed mixpanel-browser API for pre-navigation
        // sends (flush() exists at runtime but is not on OverridedMixpanel).
        mixpanel.track(eventName, props, { send_immediately: true, transport: 'sendBeacon' });
      } else {
        mixpanel.track(eventName, props);
      }
    } catch (error) {
      // ignore the error
    }
  }

  private trackWithGroups(eventName: string, props: Record<string, unknown>, attribution?: GroupAttributionProps) {
    const groupAttribution = attribution ?? extractGroupAttribution(props);
    const { organization_id, workgroup_id, workspace_id, ...rest } = props;
    const payload = {
      ...rest,
      ...buildGroupEventProps(groupAttribution),
    };

    this.track(eventName, payload);
  }

  identifyUser(userId: string) {
    if (!this.canTrack()) {
      return;
    }

    try {
      mixpanel.identify(userId);
      this.hasIdentified = true;
      this.flushPendingProfileWrites();
    } catch (error) {
      // ignore the error
    }
  }

  resetAnalytics() {
    this.userEmail = '';
    this.hasIdentified = false;
    this.pendingProfileWrites = [];

    if (!this.initialized) {
      return;
    }

    try {
      mixpanel.reset();
    } catch (error) {
      // ignore the error
    }
  }

  private flushPendingProfileWrites() {
    const queued = this.pendingProfileWrites;
    this.pendingProfileWrites = [];
    queued.forEach((write) => this.runProfileWrite(write));
  }

  private runProfileWrite(write: () => void) {
    try {
      write();
    } catch (error) {
      // ignore the error
    }
  }

  /**
   * Defer profile writes until identify() has run so they land on the identified
   * distinct_id instead of the transient pre-login anonymous one.
   */
  private writeProfile(write: () => void) {
    if (!this.canTrack()) {
      return;
    }

    if (!this.hasIdentified) {
      this.pendingProfileWrites.push(write);
      return;
    }

    this.runProfileWrite(write);
  }

  syncUserProfile(input: SyncUserProfileInput) {
    this.writeProfile(() => updateUserProfile(input));
  }

  /** Record the onboarding path once the Get Started submit has succeeded. */
  trackOnboardingPathSelected(onboardingPath: OnboardingPath) {
    this.writeProfile(() => updateOnboardingPath(onboardingPath));
  }

  trackLoginComplete(userProfile: UserInfo) {
    const { id, email } = userProfile;

    this.setUserEmail(email);

    if (id && this.canTrack()) {
      mixpanel.opt_in_tracking();
      this.identifyUser(id);
      this.syncUserProfile({ userInfo: userProfile });
    }
  }

  trackPageViewed(props: PageViewedProps, options?: { flush?: boolean }) {
    const { organization_id, ...rest } = props;
    const payload = {
      ...rest,
      ...buildGroupEventProps({ organization_id }),
    };
    this.track('page_viewed', payload, options);
  }

  trackGraphResultsViewed(props: GraphResultsViewedProps) {
    this.trackWithGroups('graph_results_viewed', { ...props });
  }

  trackSchemaModified(props: SchemaModifiedProps) {
    this.trackWithGroups('schema_modified', { ...props });
  }

  // Canonical replacements for the removed legacy events. See analytics/types.ts for why
  // these are emitted from the browser rather than from a backend service.

  trackSignInCompleted(props: SignInCompletedProps) {
    this.trackWithGroups('sign_in_completed', { ...props });
  }

  trackGetStartedCompleted(props: GetStartedCompletedProps) {
    this.trackWithGroups('get_started_completed', { ...props });
  }

  trackGraphCreated(props: GraphCreatedProps) {
    this.trackWithGroups('graph_created', { ...props });
  }

  trackSchemaSaved(props: SchemaSavedProps) {
    this.trackWithGroups('schema_saved', { ...props });
  }

  trackDataLoadCompleted(props: DataLoadCompletedProps) {
    this.trackWithGroups('data_load_completed', { ...props });
  }

  trackQueryInstalled(props: QueryInstalledProps) {
    this.trackWithGroups('query_installed', { ...props });
  }

  trackQueryRunCompleted(props: QueryRunCompletedProps) {
    this.trackWithGroups('query_run_completed', { ...props });
  }

  /** EVT-004 is user-scoped: the spec assigns it no group attribution. */
  trackTermsAccepted(props: TermsAcceptedProps, options?: { flush?: boolean }) {
    this.track('terms_accepted', props, options);
  }

  trackGraphDeleted(props: GraphDeletedProps) {
    this.trackWithGroups('graph_deleted', { ...props });
  }

  trackOrganizationInvitationCreated(props: OrganizationInvitationCreatedProps) {
    this.trackWithGroups('organization_invitation_created', { ...props });
  }
}

const trackUtil = new AnalyticsService();
export default trackUtil;
